import React from 'react';
import {
  Box,
  Typography,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Modal,
  IconButton,
  Chip,
  Divider,
  LinearProgress,
} from '@mui/material';
import { Visibility, Close, DoneAll } from '@mui/icons-material';

const getInitials = (name) => {
  if (!name) return '?';
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

const UserRow = ({ user, seen }) => (
  <ListItem
    sx={{
      px: 1.5,
      py: 1,
      borderRadius: 2,
      '&:hover': { bgcolor: '#F8FAFC' },
      transition: 'background-color 0.15s ease',
    }}
  >
    <ListItemAvatar sx={{ minWidth: 44 }}>
      <Box sx={{ position: 'relative' }}>
        <Avatar
          sx={{
            width: 36,
            height: 36,
            bgcolor: seen ? '#1570EF' : '#CBD5E1',
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          {getInitials(`${user.first_name || ''} ${user.last_name || ''}`)}
        </Avatar>
        <Box
          sx={{
            position: 'absolute',
            bottom: -1,
            right: -1,
            width: 12,
            height: 12,
            borderRadius: '50%',
            bgcolor: seen ? '#10B981' : '#94A3B8',
            border: '2px solid #FFFFFF',
          }}
        />
      </Box>
    </ListItemAvatar>
    <ListItemText
      primary={
        <Typography
          variant="body2"
          fontWeight={600}
          color="#0F172A"
          sx={{ fontSize: 13.5 }}
        >
          {user.first_name} {user.last_name}
        </Typography>
      }
      secondary={
        <Typography variant="caption" color="#94A3B8" sx={{ fontSize: 11.5 }}>
          {user.email || user.username}
        </Typography>
      }
    />
    {user.user_type && (
      <Chip
        label={user.user_type}
        size="small"
        sx={{
          fontSize: 10,
          height: 22,
          fontWeight: 600,
          bgcolor: seen ? '#EFF6FF' : '#F1F5F9',
          color: seen ? '#1570EF' : '#64748B',
          borderRadius: 1.5,
        }}
      />
    )}
  </ListItem>
);

const ReadReceipts = ({ message, open, onClose, participants }) => {
  if (!message) return null;

  const readUsers = message.read_by || [];
  const totalParticipants = participants?.length || 0;
  const unreadUsers =
    participants?.filter((p) => !readUsers.some((r) => r.id === p.id)) || [];
  const readPercent =
    totalParticipants > 0 ? (readUsers.length / totalParticipants) * 100 : 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="read-receipts-modal"
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <Box
        sx={{
          bgcolor: '#FFFFFF',
          borderRadius: 3,
          boxShadow: '0 24px 64px rgba(15, 23, 42, 0.18)',
          maxWidth: 420,
          width: '92%',
          maxHeight: '82vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          outline: 'none',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 2.5,
            py: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid #F1F5F9',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: '#EFF6FF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <DoneAll sx={{ color: '#1570EF', fontSize: 22 }} />
            </Box>
            <Box>
              <Typography
                variant="h6"
                fontWeight={700}
                color="#0F172A"
                sx={{ fontSize: 17, lineHeight: 1.2 }}
              >
                Read Receipts
              </Typography>
              <Typography
                variant="caption"
                color="#64748B"
                sx={{ fontSize: 12, fontWeight: 500 }}
              >
                {readUsers.length} of {totalParticipants} participants
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: '#64748B',
              bgcolor: '#F1F5F9',
              '&:hover': { bgcolor: '#E2E8F0' },
              width: 32,
              height: 32,
            }}
          >
            <Close sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        {/* Progress bar */}
        <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 0.75,
            }}
          >
            <Typography
              variant="caption"
              color="#64748B"
              sx={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.4 }}
            >
              DELIVERY STATUS
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontSize: 11,
                fontWeight: 700,
                color: readPercent === 100 ? '#10B981' : '#1570EF',
              }}
            >
              {Math.round(readPercent)}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={readPercent}
            sx={{
              height: 6,
              borderRadius: 3,
              bgcolor: '#F1F5F9',
              '& .MuiLinearProgress-bar': {
                borderRadius: 3,
                bgcolor: readPercent === 100 ? '#10B981' : '#1570EF',
              },
            }}
          />
        </Box>

        {/* Body */}
        <Box
          sx={{
            px: 1.5,
            py: 1.5,
            overflow: 'auto',
            flex: 1,
            '&::-webkit-scrollbar': { width: 6 },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: '#CBD5E1',
              borderRadius: 3,
            },
          }}
        >
          {/* Seen */}
          <Box sx={{ mb: unreadUsers.length > 0 ? 1 : 0 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                px: 1.5,
                py: 1,
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: '#10B981',
                }}
              />
              <Typography
                variant="caption"
                fontWeight={700}
                color="#0F172A"
                sx={{ fontSize: 11.5, letterSpacing: 0.4 }}
              >
                SEEN BY · {readUsers.length}
              </Typography>
            </Box>
            {readUsers.length === 0 ? (
              <Typography
                variant="body2"
                color="#94A3B8"
                sx={{ px: 1.5, py: 1.5, fontSize: 13, fontStyle: 'italic' }}
              >
                No one has seen this message yet
              </Typography>
            ) : (
              <List sx={{ p: 0 }}>
                {readUsers.map((user) => (
                  <UserRow key={user.id} user={user} seen />
                ))}
              </List>
            )}
          </Box>

          {/* Unread */}
          {unreadUsers.length > 0 && (
            <>
              <Divider sx={{ my: 1, borderColor: '#F1F5F9' }} />
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    px: 1.5,
                    py: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: '#94A3B8',
                    }}
                  />
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    color="#0F172A"
                    sx={{ fontSize: 11.5, letterSpacing: 0.4 }}
                  >
                    NOT SEEN · {unreadUsers.length}
                  </Typography>
                </Box>
                <List sx={{ p: 0 }}>
                  {unreadUsers.map((user) => (
                    <UserRow key={user.id} user={user} seen={false} />
                  ))}
                </List>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default ReadReceipts;